import React, { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Home } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { ChatBubble } from "@/components/onboarding/ChatBubble";
import { profileService } from "@/services/profileService";
import { analytics } from "@/hooks/useAnalytics";

export default function ThankYouPage() {
  const { user, organization, userProfile } = useAuth();
  const navigate = useNavigate();
  const [showMessage, setShowMessage] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const [firstName, setFirstName] = useState<string>("");
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Load first name from profile
  useEffect(() => {
    const loadFirstName = async () => {
      try {
        // First try to get from userProfile if available (snake_case from database)
        if (userProfile?.first_name) {
          setFirstName(userProfile.first_name);
          return;
        }

        // Otherwise fetch from profileService (camelCase from service interface)
        if (user?.id) {
          const result = await profileService.getUserProfile(user.id);
          if (result.success && result.data?.firstName) {
            setFirstName(result.data.firstName);
          }
        }
      } catch (error) {
        console.error("Error loading first name:", error);
        // Silently fail - firstName will remain empty string and fallback to "friend"
      }
    };

    loadFirstName();
  }, [user?.id, userProfile]);

  useEffect(() => {
    // Track Purchase event for Meta Pixel / GTM
    if (user && organization) {
      analytics.trackPurchase({
        value: 90,
        currency: "USD",
        content_ids: ["hubflo-subscription"],
        contents: [{ id: "hubflo-subscription", quantity: 1 }],
        content_type: "product",
        content_name: "Hubflo AI Subscription",
        content_category: "subscription",
        num_items: 1,
        organization_id: organization.id,
        organization_name: organization.name,
        user_id: user.id,
      });
    }

    // Trigger confetti animation on mount
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });

    // Show message after a short delay
    timeoutIdRef.current = setTimeout(() => {
      setShowMessage(true);
    }, 500);

    // Cleanup function
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [user, organization]);

  const handleTypingComplete = () => {
    // Stop the typing animation
    setIsTyping(false);

    // Show button after typing completes
    // Note: This timeout is short-lived and triggered by user interaction,
    // so it's less critical to clean up, but we could store it if needed
    setTimeout(() => {
      setShowButton(true);
    }, 300);
  };

  const handleOpenDashboard = () => {
    navigate("/ai-assistants");
  };

  const thankYouMessage = `🎉 Welcome aboard, ${firstName || "friend"}!

Your 3-day free trial is now active. Let's make every moment count!

Browse our collection of ready-to-use AI assistants and start using them to streamline your daily tasks.

Let's get started!

(You can manage your subscription anytime from your settings)`;

  return (
    <div className="min-h-screen bg-gradient-to-t from-amber-100/30 via-amber-50/20 to-white relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute -left-16 -top-16 w-32 h-32 bg-gradient-to-t from-yellow-400 to-amber-300 rounded-full opacity-10 blur-2xl"></div>
      <div className="absolute -right-16 -bottom-16 w-32 h-32 bg-gradient-to-t from-amber-400 to-yellow-300 rounded-full opacity-10 blur-2xl"></div>

      <div className="flex-1 flex flex-col justify-center items-center px-4 relative z-10 min-h-screen">
        <div className="max-w-2xl w-full">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/hubflo%20agents%20horizontal%20dark.svg`}
              alt="Hubflo Agents Logo"
              className="h-[50px] w-auto"
            />
          </div>

          {/* Chat bubble with avatar */}
          {showMessage && (
            <ChatBubble
              message={thankYouMessage}
              isTyping={isTyping}
              onTypingComplete={handleTypingComplete}
              showAvatar={true}
            />
          )}

          {/* Action button */}
          {showButton && (
            <div className="flex justify-center mt-6 animate-fade-in">
              <Button
                size="lg"
                onClick={handleOpenDashboard}
                className="bg-[#ecb22d] hover:bg-[#d9a429] text-white px-8 py-3 text-lg font-medium shadow-sm transition-all duration-200 hover:shadow-md"
              >
                <Home className="mr-2 h-5 w-5" />
                Open Dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
